import { pointInPolygon, polygonBounds } from "./geometry.js";
/**
 * La silueta de una puerta.
 *
 * Antes toda puerta era el rectángulo crudo, y un mundo entero de teletransportadores
 * cuadrados se ve a plantilla. Hay dos niveles:
 *
 *  - **Formas con nombre** (`arch`, `ellipse`, `diamond`, `hexagon`) inscritas en
 *    `door.rect`. Cubren el 90% de los casos con dos clics y sin geometría a mano.
 *  - **`polygon`**, que se moldea vértice a vértice igual que el piso, para cuando
 *    la puerta tiene que seguir una escalera, un arco del fondo o un charco.
 *
 * `door.rect` sigue siendo la caja contenedora en los dos casos: es lo que se arrastra
 * y redimensiona, dónde se ancla la etiqueta y de dónde sale el orden de dibujo. Para
 * un polígono es su bounding box, y el editor la recalcula en cada cambio.
 *
 * La regla que sostiene el diseño: dibujo, clic y validación salen todos de
 * `shapeOutline`. Una sola definición del contorno significa que lo que se ve es
 * exactamente lo que se puede pisar — si el hit test tuviera su propia fórmula,
 * tarde o temprano se separarían y habría puertas que se ven pero no se cruzan.
 */
export const DOOR_SHAPES = [
    "rect",
    "arch",
    "ellipse",
    "diamond",
    "hexagon",
    "polygon",
];
export const DEFAULT_DOOR_SHAPE = "rect";
/** Mínimo de vértices de una puerta moldeada, igual que el piso. */
export const MIN_DOOR_VERTICES = 3;
/** Etiquetas para la UI del editor. */
export const DOOR_SHAPE_LABELS = {
    rect: "Rectángulo",
    arch: "Arco",
    ellipse: "Elipse",
    diamond: "Rombo",
    hexagon: "Hexágono",
    polygon: "Libre (moldeable)",
};
export function isDoorShape(value) {
    return typeof value === "string" && DOOR_SHAPES.includes(value);
}
/**
 * Cuántos segmentos aproximan una curva.
 *
 * A 960x540 una puerta rara vez pasa de 200px de ancho, así que 24 segmentos dejan
 * lados de pocos píxeles: se ve curva y el hit test coincide con el dibujo. Subirlo
 * no mejora nada visible y sólo alarga el polígono que recorre `pointInPolygon`.
 */
const CURVE_SEGMENTS = 24;
/**
 * El contorno de la puerta, en coordenadas de mundo.
 *
 * Devuelve un polígono cerrado implícitamente (sin repetir el primer punto), que es
 * lo que esperan `pointInPolygon` y `Graphics.poly`.
 *
 * `points` sólo se usa con `shape: "polygon"`. Si falta o es degenerado se cae al
 * rectángulo: una puerta mal escrita a mano sigue siendo cruzable en vez de
 * convertirse en una zona de área cero que nadie puede pisar y nadie entiende.
 */
export function shapeOutline(rect, shape = DEFAULT_DOOR_SHAPE, points) {
    const { x, y, width, height } = rect;
    const right = x + width;
    const bottom = y + height;
    const cx = x + width / 2;
    const cy = y + height / 2;
    switch (shape) {
        case "polygon":
            if (points && points.length >= MIN_DOOR_VERTICES) {
                return points.map((point) => ({ x: point.x, y: point.y }));
            }
            break;
        case "ellipse": {
            const rx = width / 2;
            const ry = height / 2;
            const outline = [];
            for (let i = 0; i < CURVE_SEGMENTS; i++) {
                const angle = (i / CURVE_SEGMENTS) * Math.PI * 2;
                outline.push({ x: cx + Math.cos(angle) * rx, y: cy + Math.sin(angle) * ry });
            }
            return outline;
        }
        case "diamond":
            return [
                { x: cx, y },
                { x: right, y: cy },
                { x: cx, y: bottom },
                { x, y: cy },
            ];
        case "hexagon": {
            // Hexágono de punta a los lados: los cortes a un cuarto del ancho dan la
            // proporción que se lee como hexágono aunque la caja no sea cuadrada.
            const inset = width / 4;
            return [
                { x: x + inset, y },
                { x: right - inset, y },
                { x: right, y: cy },
                { x: right - inset, y: bottom },
                { x: x + inset, y: bottom },
                { x, y: cy },
            ];
        }
        case "arch": {
            /*
             * Base plana y bóveda arriba: la forma que de verdad parece una puerta.
             *
             * Se recorre desde la esquina inferior izquierda hacia arriba, se traza el
             * medio arco de izquierda a derecha y se baja. El radio horizontal es medio
             * ancho y el vertical medio alto, así que una caja ancha da un arco rebajado
             * y una angosta uno de medio punto, sin casos especiales.
             */
            const rx = width / 2;
            const ry = height / 2;
            const outline = [{ x, y: bottom }, { x, y: cy }];
            for (let i = 0; i <= CURVE_SEGMENTS / 2; i++) {
                const angle = Math.PI - (i / (CURVE_SEGMENTS / 2)) * Math.PI;
                outline.push({ x: cx + Math.cos(angle) * rx, y: cy - Math.sin(angle) * ry });
            }
            outline.push({ x: right, y: bottom });
            return outline;
        }
    }
    return [
        { x, y },
        { x: right, y },
        { x: right, y: bottom },
        { x, y: bottom },
    ];
}
/** ¿El punto está dentro? Sale del mismo contorno que se dibuja, a propósito. */
export function shapeContains(rect, shape, points, point) {
    return pointInPolygon(shapeOutline(rect, shape ?? DEFAULT_DOOR_SHAPE, points), point);
}
/**
 * La caja que corresponde a un contorno moldeado.
 *
 * El editor la recalcula en cada cambio de vértices para que `rect` no se quede
 * viejo: de ahí salen la etiqueta y el orden de dibujo, así que una caja rancia se
 * nota como un cartel flotando en el lugar equivocado.
 */
export function boundsOfPoints(points) {
    const bounds = polygonBounds(points);
    return {
        x: Math.round(bounds.x),
        y: Math.round(bounds.y),
        width: Math.max(1, Math.round(bounds.width)),
        height: Math.max(1, Math.round(bounds.height)),
    };
}
//# sourceMappingURL=doorShape.js.map